import * as THREE from "three";
import { TRANSITION_SECONDS, transitionTimeline } from "./transitionTimeline";
import { engraveWall, engravingsEnabled } from "./wallEngravings";

const BLUE = 0x20a5bb;
const CORAL = 0xff554d;
const REVEAL_SECONDS = 3.6;
const smooth = value => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

function beveledBlock(width, height, depth) {
  const shape = new THREE.Shape();
  shape.moveTo(-width / 2, -height / 2);
  shape.lineTo(width / 2, -height / 2);
  shape.lineTo(width / 2, height / 2);
  shape.lineTo(-width / 2, height / 2);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth, bevelEnabled: true, bevelThickness: .055, bevelSize: .055, bevelSegments: 2,
    steps: 1, curveSegments: 1,
  });
  geometry.translate(0, 0, -depth / 2);
  return geometry;
}

function printGrainTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1024;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Print-grain material canvas is unavailable");
  const pixels = context.createImageData(1024, 1024);
  let seed = 1627;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  for (let index = 0; index < pixels.data.length; index += 4) {
    const value = 232 + Math.floor(random() * 24);
    pixels.data[index] = pixels.data[index + 1] = pixels.data[index + 2] = value;
    pixels.data[index + 3] = 255;
  }
  context.putImageData(pixels, 0, 0);
  context.strokeStyle = "#c4c4c4";
  context.lineWidth = .6;
  for (let index = 0; index < 310; index++) {
    const x = random() * 1024;
    const y = random() * 1024;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + 2 + random() * 18, y + random() * 1.8);
    context.stroke();
  }
  context.fillStyle = "#bcbcbc";
  for (let index = 0; index < 2300; index++) {
    context.fillRect(random() * 1024, random() * 1024, 2 + random() * 4, 2 + random() * 3);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1.1, 1.7);
  texture.anisotropy = 4;
  return texture;
}

export function createThresholdScene(host, onError, destinationHost) {
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "low-power" });
  } catch (error) {
    console.error("The Threshold requires WebGL:", error);
    onError("The vault animation is unavailable on this device. You can still sign in below.");
    return {
      dispose() {}, setStill() {}, replay() {}, skip() {}, setFocus() {}, pulse() {},
      enter(complete) { complete(); },
    };
  }
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x07090b);
  scene.fog = new THREE.FogExp2(0x07090b, .018);
  const camera = new THREE.PerspectiveCamera(43, 1, .1, 110);
  const texture = printGrainTexture();
  const materials = [];
  const geometries = [];
  const listeners = [];
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  let disposed = false;
  let contextLost = false;
  let visible = true;
  let still = false;
  let focus = 0;
  let focusTarget = 0;
  let seamHover = 0;
  let lightPointer = null;
  const projectedSeam = new THREE.Vector3();
  const projectedSeamTop = new THREE.Vector3();
  let frame = 0;
  let lastFrame = 0;
  let elapsed = 0;
  let pulseAt = -100;
  let reveal = 0;
  let pointer = { x: 0, y: 0 };
  let target = { x: 0, y: 0 };
  let width = 1;
  let height = 1;
  let shadowTargetX = NaN;
  let transitioning = false;
  let transitionElapsed = 0;
  let transitionComplete = null;
  const entryPosition = new THREE.Vector3();
  const entryRotation = new THREE.Quaternion();

  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));
  renderer.setSize(1, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.22;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.domElement.setAttribute("aria-hidden", "true");
  host.appendChild(renderer.domElement);

  const material = options => {
    const result = new THREE.MeshStandardMaterial(options);
    materials.push(result);
    return result;
  };
  const stone = material({ color: 0x454c4e, roughness: .94, metalness: .04, map: texture, bumpMap: texture, bumpScale: .018 });
  const revealStone = material({ color: 0x5b6261, roughness: .9, metalness: .06, map: texture, bumpMap: texture, bumpScale: .014 });
  const floorMaterial = material({ color: 0x252d30, roughness: .49, metalness: .24, bumpMap: texture, bumpScale: .008 });
  const metal = material({ color: 0x10191c, roughness: .5, metalness: .65 });
  const blueEmission = new THREE.ShaderMaterial({
    uniforms: { uIntensity: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float uIntensity;
      void main() {
        float fade = smoothstep(0.0, 0.18, vUv.y) * smoothstep(0.0, 0.14, 1.0 - vUv.y);
        gl_FragColor = vec4(0.125, 0.647, 0.733, fade * uIntensity);
      }
    `,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  materials.push(blueEmission);

  const leftFrame = new THREE.Group();
  const rightFrame = new THREE.Group();
  scene.add(leftFrame, rightFrame);
  function block(w, h, d, x, y, z, surface = stone, bevel = false) {
    const geometry = bevel ? beveledBlock(w, h, d) : new THREE.BoxGeometry(w, h, d);
    geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, surface);
    mesh.position.set(x, y, z);
    mesh.castShadow = mesh.receiveShadow = true;
    const frame = y >= 0 && z >= -12 ? (x < -1.2 ? leftFrame : x > 2.65 ? rightFrame : scene) : scene;
    frame.add(mesh);
    return mesh;
  }

  // A single built environment: two deep walls, a lintel, a passage and its floor.
  block(30, .5, 85, 0, -.3, -18, floorMaterial);
  block(15, 25, 9, -8.8, 12.45, -7, stone, true);
  const engravedWall = engravingsEnabled() ? material({
    color: 0x454c4e, roughness: .94, metalness: .04,
    map: texture, bumpMap: texture, bumpScale: .018,
  }) : stone;
  const disposeEngravings = engravingsEnabled()
    ? engraveWall(engravedWall) : null;
  host.dataset.engravings = engravingsEnabled() ? "on" : "off";
  block(8.5, 25, 9, 7, 12.45, -7, engravedWall, true);
  block(4.05, 12, 9, .725, 17, -7, revealStone, true);
  block(.15, 11.1, 8.8, -1.24, 5.5, -7, metal);
  block(.15, 11.1, 8.8, 2.69, 5.5, -7, metal);
  const passageStone = material({ color: 0x454c4e, roughness: .94, metalness: .04, map: texture, bumpMap: texture, bumpScale: .018 });
  block(7, 19, 40, -4.9, 9.5, -31, passageStone);
  block(7, 19, 40, 6.4, 9.5, -31, passageStone);
  block(4.3, .6, 40, .75, 12.3, -31, metal);
  block(4.2, 18, 1, .75, 9, -51, stone);
  // Recessed, fixed lighting channels belong to the architecture, not floating props.
  const wallChannel = new THREE.Mesh(new THREE.PlaneGeometry(.024, 10.5), blueEmission);
  wallChannel.position.set(2.55, 5.3, -2.99);
  const floorChannel = new THREE.Mesh(new THREE.PlaneGeometry(.018, 31), blueEmission);
  floorChannel.rotation.x = -Math.PI / 2;
  floorChannel.position.set(-1.1, -.024, -18.5);
  geometries.push(wallChannel.geometry, floorChannel.geometry);
  rightFrame.add(wallChannel);
  scene.add(floorChannel);
  for (const z of [3, -3, -11, -20, -31, -43]) {
    block(27, .008, .025, 0, -.043, z, metal);
  }
  // Expansion joints and recessed ties give the surfaces scale at grazing angles.
  for (const y of [2.8, 6.9, 11]) {
    block(8.4, .022, .025, 7, y, -2.47, metal);
    block(14.9, .022, .025, -8.8, y, -2.47, metal);
  }
  block(.06, 25, .05, 5.9, 12.4, -2.45, metal);
  block(.9, 25, 4, 13, 12.45, 6, metal);
  // Fixed reliefs, door rebates and footings are cut into the same structural plane.
  block(.12, 11, .18, 2.89, 5.45, -2.55, revealStone, true);
  block(.12, 11, .18, -1.45, 5.45, -2.55, revealStone, true);
  block(8.45, .14, .22, 7, .03, -2.53, metal);
  block(14.95, .14, .22, -8.8, .03, -2.53, metal);
  const engravingGeometry = new THREE.TorusGeometry(.3, .005, 4, 64);
  geometries.push(engravingGeometry);
  const engravingMaterial = material({ color: 0x354348, roughness: .88, metalness: .12 });
  const engraving = new THREE.Mesh(engravingGeometry, engravingMaterial);
  engraving.position.set(4.55, 4.9, -2.425);
  engraving.castShadow = engraving.receiveShadow = true;
  rightFrame.add(engraving);
  block(.09, .008, .012, 4.55, 4.9, -2.44, engravingMaterial);
  for (const x of [4.08, 4.2, 4.32]) block(.045, .012, .015, x, 4.3, -2.435, revealStone);
  const signalMaterial = material({ color: BLUE, emissive: BLUE, emissiveIntensity: .3, roughness: .65 });
  block(.027, .045, .025, 2.88, 2.8, -2.43, signalMaterial);

  const scratchPoints = [];
  const scratchColors = [];
  const scratchTimes = [];
  const scratchOpacities = [];
  const scratchTransient = [];
  function scratch(path, color, opacity, delay, duration, transient = false) {
    const tint = new THREE.Color(color);
    const lengths = [0];
    for (let index = 1; index < path.length; index++) {
      lengths.push(lengths[index - 1] + Math.hypot(
        path[index][0] - path[index - 1][0],
        path[index][1] - path[index - 1][1],
        (path[index][2] ?? -2.405) - (path[index - 1][2] ?? -2.405)));
    }
    const totalLength = lengths[lengths.length - 1];
    for (let index = 1; index < path.length; index++) {
      for (const point of [index - 1, index]) {
        scratchPoints.push(path[point][0], path[point][1], path[point][2] ?? -2.405);
        scratchColors.push(tint.r, tint.g, tint.b);
        scratchTimes.push(delay + lengths[point] / totalLength * duration);
        scratchOpacities.push(opacity);
        scratchTransient.push(transient ? 1 : 0);
      }
    }
  }
  scratch([[-1.67, 1.25], [-1.69, 2.04], [-1.65, 2.72]], 0xe8e2db, .065, 1.4, .8);
  scratch([[-1.66, 3.04], [-1.68, 4.15], [-1.655, 5.12], [-1.67, 6.42]], 0xe8e2db, .085, 1.9, 1.1);
  scratch([[-1.68, 6.75], [-1.65, 7.58], [-1.67, 8.9]], 0x20a5bb, .1, 2.3, .7);
  scratch([[-1.74, 3.26], [-1.72, 3.83]], 0xe8e2db, .045, 2.1, .3);
  scratch([[-1.83, 3.91], [-1.72, 3.96], [-1.76, 4.02], [-1.63, 4.005]], 0xe8e2db, .11, 1.7, .4);
  scratch([[-1.71, 6.81], [-1.61, 6.79], [-1.63, 6.9]], 0xff554d, .1, 2.6, .35);
  scratch([[-1.88, 5.54], [-1.8, 5.6]], 0x20a5bb, .12, 2.2, .2);
  scratch([[4.09, 2.81], [4.13, 2.89], [4.105, 2.94], [4.19, 2.91]], 0xe8e2db, .095, 2.0, .45);
  scratch([[4.24, 2.86], [4.26, 2.96]], 0xe8e2db, .06, 2.45, .2);
  scratch([[5.79, 6.87], [5.9, 6.9], [5.85, 6.93]], 0x20a5bb, .13, 1.8, .35);
  scratch([[5.96, 6.84], [5.99, 6.96]], 0xe8e2db, .075, 2.4, .25);
  scratch([[3.37, 4.32], [3.43, 4.34], [3.48, 4.315], [3.51, 4.35]], 0xff554d, .07, 2.7, .4);
  scratch([[4.86, 5.61], [4.91, 5.66], [4.98, 5.65]], 0xe8e2db, .055, 2.25, .3);
  scratch([[6.4, 2.77], [6.46, 2.79], [6.5, 2.77], [6.57, 2.8]], 0x20a5bb, .08, 2.8, .35);
  scratch([[3.15, -.018, -1.85], [4.24, -.018, -1.82], [5.06, -.018, -1.86], [6.65, -.018, -1.84]], 0xe8e2db, .075, 2.2, .9);
  scratch([[6.98, -.021, -1.82], [7.42, -.021, -1.8], [8.21, -.021, -1.84]], 0x20a5bb, .055, 2.5, .65);
  scratch([[2.96, .28], [3.0, .88], [2.985, 1.25]], 0xe8e2db, .06, 2.0, .55);
  scratch([[1.55, -.018, -.3], [2.38, -.018, -.33], [2.91, -.018, -.3]], 0xe8e2db, .045, 2.65, .7);
  scratch([[5.62, .02, -3.0], [6.23, .02, -1.4], [6.59, .02, -.7]], 0xe8e2db, .06, 2.2, .75);
  scratch([[2.48, -.018, 1.2], [2.49, -.018, -4.5], [2.47, -.018, -12], [2.49, -.018, -23.5]], 0xe8e2db, .09, 2.3, 1);
  scratch([[2.74, .04, 1.6], [2.74, .04, -8], [2.74, .04, -20]], 0x20a5bb, .035, 2.8, 1.1);
  scratch([[3.03, .24, -2.4], [3.03, 3.8, -2.4], [3.03, 7.6, -2.4]], 0xe8e2db, .035, 2.1, .9);
  scratch([[3.08, .22, -2.4], [3.34, .22, -2.4], [3.34, .38, -2.4]], 0xe8e2db, .05, 2.8, .4);
  scratch([[5.25, .035, -.4], [5.25, .035, -8], [5.25, .035, -20]], 0x20a5bb, .045, 1, .7, true);
  scratch([[2.96, 1.1, -2.4], [2.96, 3.6, -2.4], [2.96, 6.9, -2.4]], 0xe8e2db, .04, 9, .55, true);
  const scratchGeometry = new THREE.BufferGeometry();
  scratchGeometry.setAttribute("position", new THREE.Float32BufferAttribute(scratchPoints, 3));
  scratchGeometry.setAttribute("aColor", new THREE.Float32BufferAttribute(scratchColors, 3));
  scratchGeometry.setAttribute("aTime", new THREE.Float32BufferAttribute(scratchTimes, 1));
  scratchGeometry.setAttribute("aOpacity", new THREE.Float32BufferAttribute(scratchOpacities, 1));
  scratchGeometry.setAttribute("aTransient", new THREE.Float32BufferAttribute(scratchTransient, 1));
  const scratchMaterial = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uRelease: { value: 0 } },
    vertexShader: `
      attribute vec3 aColor;
      attribute float aTime;
      attribute float aOpacity;
      attribute float aTransient;
      uniform float uRelease;
      varying vec3 vColor;
      varying float vTime;
      varying float vOpacity;
      varying float vTransient;
      void main() {
        vColor = aColor;
        vTime = aTime;
        vOpacity = aOpacity;
        vTransient = aTransient;
        vec3 released = position;
        released.x += (position.x < 0.0 ? -1.0 : 1.0) * uRelease;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(released, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      varying vec3 vColor;
      varying float vTime;
      varying float vOpacity;
      varying float vTransient;
      void main() {
        float ink = smoothstep(vTime, vTime + 0.3, uTime);
        float travel = mod(max(0.0, uTime - 6.0), 19.0);
        float wisp = (1.0 - smoothstep(0.0, 0.22, abs(travel - vTime))) * step(6.0, uTime);
        ink = mix(ink, wisp, vTransient);
        gl_FragColor = vec4(vColor, ink * vOpacity);
        #include <colorspace_fragment>
      }
    `,
    transparent: true, depthWrite: false,
  });
  geometries.push(scratchGeometry);
  materials.push(scratchMaterial);
  scene.add(new THREE.LineSegments(scratchGeometry, scratchMaterial));

  scene.add(new THREE.AmbientLight(0xb5c7ca, .04));
  const key = new THREE.SpotLight(0xe8e2db, 0, 46, .63, 1, 1.7);
  key.position.set(-2, 11, 5);
  key.target.position.set(4.5, 6, -5);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.bias = -.00035;
  key.shadow.normalBias = .055;
  key.shadow.camera.near = .5;
  key.shadow.camera.far = 50;
  key.shadow.camera.updateProjectionMatrix();
  scene.add(key, key.target);

  const blue = new THREE.SpotLight(BLUE, 0, 32, .72, .82, 1.6);
  blue.position.set(2.8, 7, .8);
  blue.target.position.set(3.8, 4.8, -2.5);
  scene.add(blue, blue.target);
  const seamGlowMaterial = new THREE.ShaderMaterial({
    uniforms: { uIntensity: { value: 0 }, uReveal: { value: 0 }, uCharge: { value: 0 }, uNeutral: { value: 0 },
      uTransportColor: { value: new THREE.Vector3(1, 1, 1) }, uTransport: { value: 0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float uIntensity;
      uniform float uReveal;
      uniform float uCharge;
      uniform float uNeutral;
      uniform vec3 uTransportColor;
      uniform float uTransport;
      void main() {
        float edge = abs(vUv.x - 0.5);
        float vertical = smoothstep(0.0, 0.14, vUv.y) * smoothstep(0.0, 0.12, 1.0 - vUv.y);
        float core = exp(-edge * 225.0);
        float diffusion = exp(-edge * 14.0);
        float haze = exp(-edge * 6.8) * 0.12;
        vec3 color = mix(vec3(1.0, 0.11, 0.065), vec3(1.0, 0.84, 0.68), sqrt(core) * 0.95);
        float light = (core * 2.0 + diffusion * 0.32 + haze) * vertical * uIntensity;
        float rise = 1.0 - smoothstep(uReveal - 0.15, uReveal + 0.03, vUv.y);
        vec3 threshold = mix(vec3(1.0, 0.26, 0.15), vec3(0.95, 0.98, 1.0), uNeutral) * diffusion * uCharge * 0.65;
        color = mix(color, vec3(0.85, 0.96, 0.97), core * uCharge * 0.18);
        color = mix(color, vec3(0.97, 0.985, 1.0), uNeutral);
        color = mix(color, uTransportColor, uTransport);
        threshold = mix(threshold, uTransportColor * diffusion * uCharge * 0.65, uTransport);
        float feather = 1.0 - smoothstep(0.15, 0.5, edge);
        gl_FragColor = vec4(color + threshold, (light + diffusion * uCharge * 0.22) * rise * feather);
      }
    `,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  materials.push(seamGlowMaterial);
  const seamGlow = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 8.3), seamGlowMaterial);
  geometries.push(seamGlow.geometry);
  seamGlow.position.set(.66, 4.32, -25.91);
  seamGlow.renderOrder = 2;
  scene.add(seamGlow);
  const seamSpill = new THREE.PointLight(CORAL, 3.2, 14, 2);
  seamSpill.position.set(.66, 4.26, -26.12);
  scene.add(seamSpill);
  const thresholdLight = new THREE.PointLight(BLUE, 0, 18, 2);
  thresholdLight.position.set(.66, 4.26, -23.8);
  scene.add(thresholdLight);
  const floorLight = new THREE.SpotLight(BLUE, 0, 29, .55, 1, 1.6);
  floorLight.position.set(-.6, 7, -4);
  floorLight.target.position.set(1, 0, 3);
  scene.add(floorLight, floorLight.target);
  function listen(element, event, handler, options) {
    element.addEventListener(event, handler, options);
    listeners.push(() => element.removeEventListener(event, handler, options));
  }
  function request() {
    if (!frame && !disposed && !contextLost && visible && !document.hidden) frame = requestAnimationFrame(render);
  }
  function render(now) {
    frame = 0;
    if (disposed || contextLost || !visible || document.hidden) return;
    const elapsedDelta = lastFrame ? Math.max(0, (now - lastFrame) / 1000) : 1 / 30;
    const dt = Math.min(elapsedDelta, .06);
    lastFrame = now;
    const calm = still || reduced.matches;
    if (!calm) elapsed += dt;
    if (transitioning) transitionElapsed += elapsedDelta;
    reveal = calm ? 1 : smooth(elapsed / REVEAL_SECONDS);
    scratchMaterial.uniforms.uTime.value = calm ? 8 : elapsed;
    const inertia = calm || transitioning ? 1 : 1 - Math.exp(-dt * 2.3);
    pointer.x += ((calm ? 0 : target.x) - pointer.x) * inertia;
    pointer.y += ((calm ? 0 : target.y) - pointer.y) * inertia;
    focus += (focusTarget - focus) * (calm ? 1 : 1 - Math.exp(-dt * 3));
    const approach = calm ? 1 : smooth(elapsed / REVEAL_SECONDS);
    const idle = calm || transitioning ? 0 : Math.max(0, elapsed - REVEAL_SECONDS);
    const drift = Math.sin(idle * .09) * .035;
    const mobile = width / height < .95;
    const transitionProgress = transitioning ? Math.min(1, transitionElapsed / TRANSITION_SECONDS) : 0;
    camera.position.set(1.55 + pointer.x * .11 + drift,
      2.45 - pointer.y * .065, 15.45 - approach * .65);
    camera.fov = mobile ? 55 : 43;
    camera.lookAt(mobile ? .4 : -1.1, 3.7 + (transitioning ? 0 : pointer.y * .04), -18);
    if (transitioning) {
      camera.position.copy(entryPosition);
      camera.quaternion.copy(entryRotation);
    }
    camera.updateProjectionMatrix();
    let hoverTarget = 0;
    if (lightPointer && !calm && !transitioning && reveal === 1) {
      projectedSeam.copy(seamGlow.position).project(camera);
      projectedSeamTop.copy(seamGlow.position);
      projectedSeamTop.y += 4.15;
      projectedSeamTop.project(camera);
      const dx = (lightPointer.x - projectedSeam.x) * width / 2 / 38;
      const dy = (lightPointer.y - projectedSeam.y) / Math.max(.01, Math.abs(projectedSeamTop.y - projectedSeam.y));
      hoverTarget = 1 - smooth(Math.hypot(dx, dy));
    }
    seamHover = calm || transitioning ? 0 : seamHover + (hoverTarget - seamHover) * (1 - Math.exp(-dt * 5));
    const idlePulsePhase = idle % 14;
    const idlePulse = !calm && !transitioning && idlePulsePhase >= 8 && idlePulsePhase < 10
      ? Math.sin((idlePulsePhase - 8) / 2 * Math.PI) ** 2 * .65 : 0;
    const seamResponse = Math.max(seamHover, idlePulse);
    seamGlow.scale.x = 1 + seamResponse * .2;
    const transport = transitionTimeline(transitioning ? transitionElapsed : 0);
    const release = transitioning ? transport.release : 0;
    const pulse = transitioning ? transport.charge : 0;
    const opening = 0;
    leftFrame.position.x = -opening;
    rightFrame.position.x = opening;
    const rootStyle = host.parentElement.parentElement.style;
    rootStyle.setProperty("--v6-release", String(release));
    rootStyle.setProperty("--transport-charge", String(pulse));
    rootStyle.setProperty("--transport-spread", String(transport.spread));
    rootStyle.setProperty("--transport-coverage", String(transport.coverage));
    rootStyle.setProperty("--transport-light", String(transitioning ? transport.light : 0));
    rootStyle.setProperty("--transport-recede", String(transport.recede));
    rootStyle.setProperty("--transport-aperture", String(transport.aperture));
    rootStyle.setProperty("--transport-mask-core", String(1 - transport.dissolve));
    rootStyle.setProperty("--transport-diffusion", String(transport.diffusion));
    rootStyle.setProperty("--transport-color", transport.color.join(" "));
    rootStyle.setProperty("--transport-sweep", String(transport.sweep));
    rootStyle.setProperty("--transport-dark", String(transitioning ? transport.dark : 0));
    rootStyle.setProperty("--transport-fringe", String(transitioning ? 1 - transport.neutral : 0));
    host.parentElement.style.opacity = transitioning ? String((1 - transport.recede) ** 3) : "1";
    host.parentElement.style.visibility = transitioning && transport.handedOff ? "hidden" : "visible";
    if (transitioning) {
      // Complementary soft masks uncover the selector from the edges inward.
      destinationHost.style.visibility = transport.selectorReady ? "visible" : "hidden";
      destinationHost.style.opacity = "1";
      destinationHost.style.filter = "none";
      const sourceX = rootStyle.getPropertyValue("--transport-source-x");
      const sourceY = rootStyle.getPropertyValue("--transport-source-y");
      destinationHost.style.maskImage = transport.dissolve === 1 ? "none"
        : `radial-gradient(ellipse ${transport.aperture * 150}vmax ${transport.aperture * 150}vmax at ${sourceX} ${sourceY}, rgb(0 0 0 / ${transport.dissolve}) 0%, rgb(0 0 0 / ${transport.dissolve}) 55%, black 100%)`;
      host.dataset.handoff = transport.phase;
      host.dataset.transportTime = String(transitionElapsed);
    }
    key.target.position.x = 3.2 + (transitioning ? 0 : pointer.x * .06);
    key.intensity = (calm ? 1 : smooth((elapsed - .55) / 2.7)) * 156;
    const breath = Math.sin(idle * .18) * .018;
    blue.intensity = (calm ? 1 : smooth((elapsed - .35) / 2.9)) * (82 + focus * 4) * (1 + breath);
    blue.target.position.x = 3.8 + (transitioning ? 0 : pointer.x * .06 + Math.sin(idle * .12) * .035);
    floorLight.intensity = reveal * 19;
    blueEmission.uniforms.uIntensity.value = .025 + reveal * .5;
    const seamReveal = calm ? 1.18 : smooth((elapsed - .2) / 2.5) * 1.18;
    seamGlowMaterial.uniforms.uReveal.value = seamReveal;
    seamGlowMaterial.uniforms.uIntensity.value = reveal * (1 + pulse * 4 + seamResponse * .24);
    seamGlowMaterial.uniforms.uCharge.value = pulse * 1.3;
    seamGlowMaterial.uniforms.uNeutral.value = transitioning ? transport.neutral : 0;
    seamGlowMaterial.uniforms.uTransport.value = transitioning ? 1 : 0;
    if (transitioning) {
      seamGlowMaterial.uniforms.uTransportColor.value.set(...transport.color.map(value => value / 255));
      seamSpill.color.setRGB(...transport.color.map(value => value / 255), THREE.SRGBColorSpace);
      thresholdLight.color.copy(seamSpill.color);
    }
    seamSpill.intensity = reveal * (3.2 + pulse * 20 + seamResponse * .6);
    thresholdLight.intensity = reveal * pulse * 24;
    if (!transitioning && !calm && elapsed - pulseAt < 1.2) blue.intensity += Math.sin((elapsed - pulseAt) / 1.2 * Math.PI) * 4;
    if (shadowTargetX !== key.target.position.x) {
      key.shadow.needsUpdate = true;
      shadowTargetX = key.target.position.x;
    }
    if (!transitioning || !transport.handedOff) renderer.render(scene, camera);
    host.dataset.ready = "true";
    host.dataset.motion = calm ? "still" : "cinematic";
    host.dataset.triangles = String(renderer.info.render.triangles);
    host.dataset.drawCalls = String(renderer.info.render.calls);
    host.dataset.frame = String(renderer.info.render.frame);
    host.dataset.seamReveal = String(seamReveal);
    host.dataset.seamHover = String(seamHover);
    host.dataset.seamPulse = String(idlePulse);
    host.dataset.entry = String(transitionProgress);
    host.dataset.camera = camera.position.toArray().join(",");
    host.dataset.phase = transitioning ? "transport" : reveal < 1 ? "reveal" : "settled";
    host.parentElement.dataset.phase = host.dataset.phase;
    if (transitioning && transitionProgress >= 1) {
      destinationHost.style.maskImage = "none";
      transitioning = false;
      host.parentElement.dataset.transition = "complete";
      const complete = transitionComplete;
      transitionComplete = null;
      complete?.();
    }
    if (!calm || transitioning || Math.abs(focus - focusTarget) > .005) request();
  }
  const resize = () => {
    width = Math.max(1, host.clientWidth);
    height = Math.max(1, host.clientHeight);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
    request();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(host);
  const intersection = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    lastFrame = 0;
    if (visible) request();
    else { cancelAnimationFrame(frame); frame = 0; }
  });
  intersection.observe(host);
  const pointerRoot = host.parentElement;
  listen(pointerRoot, "pointermove", event => {
    if (still || reduced.matches || event.pointerType === "touch") return;
    const sceneBounds = host.getBoundingClientRect();
    lightPointer = {
      x: (event.clientX - sceneBounds.left) / sceneBounds.width * 2 - 1,
      y: 1 - (event.clientY - sceneBounds.top) / sceneBounds.height * 2,
    };
    const bounds = pointerRoot.getBoundingClientRect();
    const limit = value => Math.max(-1, Math.min(1, value));
    target = { x: limit((event.clientX - bounds.left) / bounds.width * 2 - 1), y: limit((event.clientY - bounds.top) / bounds.height * 2 - 1) };
    request();
  }, { passive: true });
  listen(pointerRoot, "pointerleave", () => {
    lightPointer = null;
    target = { x: 0, y: 0 };
    if (!still && !reduced.matches) request();
  });
  listen(document, "visibilitychange", () => {
    cancelAnimationFrame(frame);
    frame = 0;
    lastFrame = 0;
    if (!document.hidden) request();
  });
  listen(reduced, "change", () => {
    if (reduced.matches) elapsed = 8;
    if (reduced.matches && transitioning) {
      transitioning = false;
      const complete = transitionComplete;
      transitionComplete = null;
      complete?.();
    }
    lastFrame = 0;
    request();
  });
  listen(renderer.domElement, "webglcontextlost", event => {
    event.preventDefault();
    contextLost = true;
    cancelAnimationFrame(frame);
    frame = 0;
    console.error("The Threshold WebGL context was lost");
    onError("The graphics context was interrupted. You can still sign in, or reload to restore the scene.");
  });
  resize();
  return {
    setStill(value) {
      still = value;
      if (still) elapsed = 8;
      lastFrame = 0; request();
    },
    setFocus(value) {
      focusTarget = value ? 1 : 0;
      host.parentElement.dataset.focus = String(value); request();
    },
    replay() {
      transitioning = false;
      transitionComplete = null;
      host.parentElement.dataset.transition = "";
      elapsed = 0; lastFrame = 0; pulseAt = -100;
      host.parentElement.dataset.entrance = String(Number(host.parentElement.dataset.entrance || 0) + 1);
      request();
    },
    skip() { elapsed = 8; request(); },
    pulse() { pulseAt = elapsed; request(); },
    enter(complete) {
      if (still || reduced.matches || contextLost) {
        complete();
        return;
      }
      if (transitioning) return;
      const root = host.parentElement;
      const projected = seamGlow.position.clone().project(camera);
      const hostBounds = host.getBoundingClientRect();
      const rootBounds = root.getBoundingClientRect();
      const pageX = hostBounds.left + (projected.x + 1) * .5 * hostBounds.width;
      root.parentElement.style.setProperty("--v6-source-x", `${((pageX - rootBounds.left) / rootBounds.width) * 100}%`);
      const pageY = hostBounds.top + (1 - projected.y) * .5 * hostBounds.height;
      root.parentElement.style.setProperty("--v6-source-y", `${pageY - rootBounds.top}px`);
      root.parentElement.style.setProperty("--transport-source-x", `${pageX}px`);
      root.parentElement.style.setProperty("--transport-source-y", `${pageY}px`);
      entryPosition.copy(camera.position);
      entryRotation.copy(camera.quaternion);
      thresholdLight.color.set(CORAL);
      lastFrame = 0;
      transitionElapsed = 0;
      transitionComplete = complete;
      transitioning = true;
      target = { x: 0, y: 0 };
      root.dataset.transition = "active";
      request();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      destinationHost.style.maskImage = "none";
      cancelAnimationFrame(frame);
      observer.disconnect();
      intersection.disconnect();
      listeners.forEach(remove => remove());
      geometries.forEach(geometry => geometry.dispose());
      materials.forEach(surface => surface.dispose());
      disposeEngravings?.();
      texture.dispose();
      key.shadow.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
